(function () {
    const SOURCE_KEY = "fuente_venta";
    const SOURCE_TIMESTAMP_KEY = "fecha_fuente_venta";
    const LEGACY_SOURCE_KEY = "sales_source";
    const LEGACY_SOURCE_TIMESTAMP_KEY = "sales_source_timestamp";
    const VALID_SOURCES = new Set(["catalogo", "catalogo_disponible"]);
    const viewedCatalogPages = new Set();
    const viewedProducts = new Set();
    const pendingEvents = [];
    let lastCheckoutTrackedAt = 0;
    let cartSnapshot = {};
    let flushTimer = null;
    let flushAttempts = 0;

    function getStorage() {
        try {
            return window.localStorage;
        } catch (error) {
            return null;
        }
    }

    function normalizeSource(source) {
        const normalized = String(source || "").trim();
        return VALID_SOURCES.has(normalized) ? normalized : "";
    }

    function getSourceFromPath() {
        const path = window.location.pathname.replace(/\/+$/, "");

        if (path === "/pages/catalogo") {
            return "catalogo";
        }

        if (path === "/pages/catalogo-disponible" || path.indexOf("/pages/catalogo-disponible/") === 0) {
            return "catalogo_disponible";
        }

        return "";
    }

    function getSource() {
        const storage = getStorage();
        return normalizeSource(storage?.getItem(SOURCE_KEY) || storage?.getItem(LEGACY_SOURCE_KEY));
    }

    function getActiveSource() {
        return getSource() || getSourceFromPath();
    }

    function getTimestamp() {
        const storage = getStorage();
        return storage?.getItem(SOURCE_TIMESTAMP_KEY) || storage?.getItem(LEGACY_SOURCE_TIMESTAMP_KEY) || "";
    }

    function getPrimaryMeasurementId() {
        const measurementIds = window.slideruleData?.destinations?.google_analytics_4?.measurementIds;
        return Array.isArray(measurementIds) && measurementIds.length === 1 ? measurementIds[0] : "";
    }

    function persistSource(source) {
        const normalized = normalizeSource(source);

        if (!normalized) {
            return "";
        }

        const storage = getStorage();
        const timestamp = new Date().toISOString();

        if (storage) {
            storage.setItem(SOURCE_KEY, normalized);
            storage.setItem(SOURCE_TIMESTAMP_KEY, timestamp);
            storage.removeItem(LEGACY_SOURCE_KEY);
            storage.removeItem(LEGACY_SOURCE_TIMESTAMP_KEY);
        }

        setGlobalParameters(normalized);
        return normalized;
    }

    function setGlobalParameters(source) {
        const normalized = normalizeSource(source || getSource());

        if (!normalized) {
            return;
        }

        // GA4 native Shopify events may read global parameters; this avoids sending a duplicate purchase event.
        if (typeof window.gtag === "function") {
            window.gtag("set", {
                catalog_source: normalized,
                fuente_venta: normalized
            });
        }
    }

    function queueGtagEvent(name, payload) {
        window.dataLayer = window.dataLayer || [];

        pendingEvents.push({ name, payload });
        window.dataLayer.push({
            event: name,
            ...payload
        });

        schedulePendingEventsFlush();
    }

    function trackWithSliderule(name, payload) {
        const analytics = window.slideruleData?.analytics;

        if (typeof analytics?.track !== "function") {
            return false;
        }

        analytics.track(name, payload);
        return true;
    }

    function flushPendingEvents() {
        flushTimer = null;

        if (!pendingEvents.length) {
            return;
        }

        const analyticsReady = typeof window.slideruleData?.analytics?.track === "function";
        const gtagReady = typeof window.gtag === "function";

        if (!analyticsReady && !gtagReady) {
            schedulePendingEventsFlush();
            return;
        }

        while (pendingEvents.length) {
            const event = pendingEvents.shift();

            if (analyticsReady) {
                window.slideruleData.analytics.track(event.name, event.payload);
            } else {
                window.gtag("event", event.name, event.payload);
            }
        }
    }

    function schedulePendingEventsFlush() {
        if (flushTimer || flushAttempts >= 20) {
            return;
        }

        flushAttempts += 1;
        flushTimer = window.setTimeout(flushPendingEvents, 500);
    }

    function sendEvent(name, params) {
        const source = normalizeSource(params?.catalog_source || getActiveSource());

        if (!source) {
            return;
        }

        const measurementId = getPrimaryMeasurementId();
        const payload = {
            ...params,
            ...(measurementId && !params?.send_to ? { send_to: measurementId } : {}),
            catalog_source: source
        };

        setGlobalParameters(source);

        if (typeof window.gtag === "function") {
            window.gtag("event", name, payload);
            return;
        }

        if (trackWithSliderule(name, payload)) {
            return;
        }

        queueGtagEvent(name, payload);
    }

    function normalizeProduct(product) {
        if (!product) {
            return null;
        }

        const price = Number(product.price ?? product.fullPrice ?? 0) || 0;

        return {
            item_id: String(product.reference ?? product.id ?? "").trim(),
            item_name: String(product.description ?? product.title ?? "").trim(),
            category: String(product.category ?? "").trim(),
            price
        };
    }

    async function persistCartAttribution(source) {
        const normalized = normalizeSource(source || getSource());

        if (!normalized || typeof window.fetch !== "function") {
            return false;
        }

        try {
            // Shopify copies cart attributes to the order, so this survives GA/session loss.
            const response = await window.fetch("/cart/update.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                body: JSON.stringify({
                    attributes: {
                        fuente_venta: normalized,
                        fecha_fuente_venta: getTimestamp() || new Date().toISOString()
                    }
                })
            });

            return response.ok;
        } catch (error) {
            console.warn("CatalogAnalytics: no fue posible persistir fuente_venta en Shopify.", error);
            return false;
        }
    }

    function trackView(source) {
        const normalized = persistSource(source || getSourceFromPath());

        if (!normalized) {
            return;
        }

        const key = `${normalized}:${window.location.pathname}`;

        if (viewedCatalogPages.has(key)) {
            return;
        }

        viewedCatalogPages.add(key);
        sendEvent("catalog_view", {
            catalog_source: normalized
        });
    }

    function trackProductView(product) {
        const source = getActiveSource();
        const normalizedProduct = normalizeProduct(product);

        if (!source || !normalizedProduct?.item_id) {
            return;
        }

        const key = `${source}:${normalizedProduct.item_id}`;

        if (viewedProducts.has(key)) {
            return;
        }

        viewedProducts.add(key);
        sendEvent("catalog_product_view", normalizedProduct);
    }

    function trackProductViews(products) {
        if (!Array.isArray(products)) {
            return;
        }

        products.forEach(trackProductView);
    }

    function trackAddToCart(product, cartParams = {}) {
        const source = getActiveSource();
        const normalizedProduct = normalizeProduct(product);

        if (!source || !normalizedProduct?.item_id) {
            return;
        }

        void persistCartAttribution(source);
        sendEvent("catalog_add_to_cart", {
            currency: "COP",
            value: normalizedProduct.price,
            ...cartParams,
            ...normalizedProduct
        });
    }

    function trackInteraction(eventName, params = {}) {
        const safeName = String(eventName || "").trim();

        if (!safeName) {
            return;
        }

        sendEvent(safeName, params);
    }

    function setCartSnapshot(params = {}) {
        cartSnapshot = {
            ...params
        };
    }

    function trackCheckout() {
        const source = getActiveSource();
        const now = Date.now();

        if (!source || now - lastCheckoutTrackedAt < 1500) {
            return;
        }

        lastCheckoutTrackedAt = now;
        void persistCartAttribution(source);
        sendEvent("catalog_begin_checkout", {
            catalog_source: source,
            ...cartSnapshot
        });
    }

    function isCheckoutNode(node) {
        if (!node?.matches) {
            return false;
        }

        return node.matches([
            '[name="checkout"]',
            '[href^="/checkout"]',
            '[href^="checkout"]',
            'form[action^="/checkout"] button[type="submit"]',
            'form[action^="/checkout"] input[type="submit"]',
            '[data-action="checkout-whatsapp"]'
        ].join(","));
    }

    function bindCheckoutTracking() {
        document.addEventListener("click", (event) => {
            const node = event.target.closest("a, button, input");

            if (isCheckoutNode(node)) {
                trackCheckout();
            }
        }, true);

        document.addEventListener("submit", (event) => {
            const form = event.target;
            const action = String(form?.getAttribute?.("action") || "");

            if (action.indexOf("/checkout") === 0 || form?.querySelector?.('[name="checkout"]')) {
                trackCheckout();
            }
        }, true);
    }

    function init(source) {
        const pageSource = normalizeSource(source || getSourceFromPath());

        if (pageSource) {
            trackView(pageSource);
            return;
        }

        setGlobalParameters(getSource());
    }

    window.CatalogAnalytics = {
        getSource,
        init,
        persistCartAttribution,
        setCartSnapshot,
        trackAddToCart,
        trackCheckout,
        trackInteraction,
        trackProductView,
        trackProductViews,
        trackView,
        getDebugStatus() {
            return {
                source: getActiveSource(),
                measurementId: getPrimaryMeasurementId(),
                hasGtag: typeof window.gtag === "function",
                hasSlideruleAnalytics: typeof window.slideruleData?.analytics?.track === "function",
                pendingEvents: pendingEvents.length
            };
        }
    };

    window.addEventListener("load", flushPendingEvents);
    bindCheckoutTracking();
    init();
})();
